import type { ConversationsResult } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import { tagged } from "@mirage/service-logging";
import type { GuaranteedKVStorage } from "@mirage/storage";
import { BehaviorSubject } from "rxjs";

export interface ConversationDiagnostics {
  enabled: boolean;
  logs: ConversationsResult[];
}

interface KVStorageShape {
  conversationsDiagnostics: {
    isEnabled: boolean;
  };
}

const logger = tagged("conversation-service/conversationDiagnostics");

export function createConversationDiagnostics(
  storage: GuaranteedKVStorage<KVStorageShape>,
) {
  const diagnostics$ = new BehaviorSubject<ConversationDiagnostics>({
    enabled: false,
    logs: [],
  });

  storage
    .get("conversationsDiagnostics")
    .then((storedDiagnostics) => {
      if (storedDiagnostics?.isEnabled) {
        logger.debug("Conversation diagnostics enabled");
      }
      diagnostics$.next({
        enabled: storedDiagnostics?.isEnabled ?? false,
        logs: diagnostics$.value.logs,
      });
      return;
    })
    .catch((error) => {
      logger.error("Error getting conversation diagnostics", error);
    });

  function setDiagnosticsEnabled(toEnabled: boolean) {
    diagnostics$.next({
      enabled: toEnabled,
      logs: diagnostics$.value.logs,
    });
    storage.set("conversationsDiagnostics", {
      isEnabled: toEnabled,
    });
  }

  function addDiagnosticsLog(log: ConversationsResult) {
    if (!diagnostics$.value.enabled) {
      return; // ignore logs if diagnostics are not enabled
    }

    diagnostics$.next({
      enabled: diagnostics$.value.enabled,
      logs: [...diagnostics$.value.logs, log],
    });
  }

  function clearDiagnosticsLogs() {
    diagnostics$.next({
      enabled: diagnostics$.value.enabled,
      logs: [],
    });
  }

  function observeDiagnosticsLogs() {
    return diagnostics$.asObservable();
  }

  function getDiagnosticsLogs() {
    return diagnostics$.value;
  }

  return {
    setDiagnosticsEnabled,
    addDiagnosticsLog,
    clearDiagnosticsLogs,
    observeDiagnosticsLogs,
    getDiagnosticsLogs,
  };
}
